import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { css } from '@emotion/css'
import clsx from 'clsx'
import { motion } from 'framer-motion'
import { Blocks, Plus, PlusSquare, Workflow } from 'lucide-react'
import useWindowSize from 'react-use/lib/useWindowSize'
import { VariableSizeGrid as Grid } from 'react-window'
import { z } from 'zod'

import { UpdateQuery } from '~/dataProcessor/hooks/global/router'
import { useGetAllToolkits } from '~/hooks/query/tools'
import { ErrorBoundary } from '~/ui/components/base/errorBoundary'
import { scrollBarStyle } from '~/ui/constants/style/common'
import { Error, LoadingState, NoResult } from '~/ui/containers/base/emptyStates'
import { cn } from '~/utils/cn'
import { LOCAL_STORAGE_KEYS, storage } from '~/utils/localStorage'
import { authSchemeConfig } from './authConfig'
import {
  APP_CATEGORIES_LIST,
  APP_CATEGORY_KEYS,
  APP_CATEGORY_KEYS_REVERSE,
} from './constants'
import {
  APP_SORT_OPTIONS,
  DEFAULT_APP_SORT_BY_OPTION,
  FilterBar,
} from './filterBar'

export const ZTCardType = z.enum(['toolkit_card', 'add_new_tools_card'])

export const ZTToolkitCardSchema = z.object({
  type: z.literal('toolkit_card'),
  id: z.string(),
  name: z.string(),
  description: z.string(),
  logo: z.string(),
  categories: z.array(z.object({ id: z.string(), name: z.string() })),
  auth_schemes: z.array(
    z.object({
      mode: z.string(),
    }),
  ),
  meta: z.object({
    actionsCount: z.number(),
    triggersCount: z.number(),
  }),
})

export const ZTAddNewToolsCardSchema = z.object({
  type: z.literal('add_new_tools_card'),
})

export const ZTCardSchema = z.discriminatedUnion('type', [
  ZTToolkitCardSchema,
  ZTAddNewToolsCardSchema,
])

export type ZTCard = z.infer<typeof ZTCardSchema>
export type ZTToolkitCard = z.infer<typeof ZTToolkitCardSchema>
export type ZTAddNewToolsCard = z.infer<typeof ZTAddNewToolsCardSchema>

// Define the Cell component outside the main component
const Cell = ({
  columnIndex,
  rowIndex,
  style,
  columnCount,
  data,
}: {
  columnIndex: number
  rowIndex: number
  style: React.CSSProperties
  columnCount: number
  data: ZTCard[] // Explicitly type data here
}) => {
  const index = rowIndex * columnCount + columnIndex
  const item = data[index]

  if (!item) {
    return <div style={style} key={index}></div>
  }

  const cellStyle = {
    ...style,
    paddingRight: 20,
    paddingBottom: 10,
    paddingTop: rowIndex === 0 ? 20 : 10,
  }

  switch (item.type) {
    case 'add_new_tools_card':
      return (
        <div style={cellStyle}>
          <AddNewToolsCard />
        </div>
      )
    case 'toolkit_card':
      // Type narrowing should work here
      return (
        <div style={cellStyle}>
          <ToolkitBox
            toolkitData={{
              name: item.name,
              slug: item.id,
              description: item.description || '',
              logo: item.logo || '',
              categories: item.categories
                ? item.categories.map(
                    (cat: { id: string; name: string }) => cat.name,
                  )
                : [],
              meta: {
                actionsCount: item.meta?.actionsCount || 0,
                triggersCount: item.meta?.triggersCount || 0,
              },
              auth_schemes: item.auth_schemes || [],
            }}
            key={index}
          />
        </div>
      )
    default:
      return null
  }
}

export const ToolkitList = () => {
  const { query } = useRouter()
  const category = query.category as string | undefined
  const search = query.search as string | undefined
  const page = query.page ? Number(query.page) : 1

  const {
    data: { items: toolkits = [] },
    isLoading,
    isError,
    isSuccess,
  } = useGetAllToolkits({
    page,
  })

  const {
    sortBy = storage.get(
      LOCAL_STORAGE_KEYS.APP_SORT_BY,
      DEFAULT_APP_SORT_BY_OPTION,
    ),
  } = useRouter().query

  const [betaToolkits, prodToolkits] = useMemo(() => {
    const betaToolkits = toolkits.filter((toolkit) =>
      toolkit.name.toLowerCase().endsWith('beta'),
    )
    const prodToolkits = toolkits.filter(
      (toolkit) => !toolkit.name.toLowerCase().endsWith('beta'),
    )

    const isSortedAlphabetically = sortBy === APP_SORT_OPTIONS[1]?.value
    const isSortedByTime = sortBy === APP_SORT_OPTIONS[0]?.value
    const isSortByActionsCount = sortBy === APP_SORT_OPTIONS[3]?.value
    let sortedProdToolkits = prodToolkits

    if (isSortedAlphabetically) {
      sortedProdToolkits = prodToolkits.sort((a, b) =>
        a.name.localeCompare(b.name),
      )
    } else if (isSortedByTime) {
      sortedProdToolkits = prodToolkits.sort(
        (a, b) =>
          new Date(b.meta.created_at || '').getTime() -
          new Date(a.meta.created_at || '').getTime(),
      )
    } else if (isSortByActionsCount) {
      sortedProdToolkits = prodToolkits.sort(
        (a, b) =>
          (b.meta.triggers_count || 0) +
          (b.meta.tools_count || 0) -
          ((a.meta.triggers_count || 0) + (a.meta.tools_count || 0)),
      )
    }

    return [betaToolkits, sortedProdToolkits]
  }, [toolkits, sortBy])

  const { width, height } = useWindowSize()

  const [columnCount, setColumnCount] = useState(() => {
    if (typeof window === 'undefined') return 3

    return window.innerWidth < 768 ? 2 : window.innerWidth < 1000 ? 3 : 3
  })

  useEffect(() => {
    const handleResize = () => {
      const newColumnCount =
        window.innerWidth < 768
          ? 2
          : window.innerWidth < 1100
            ? 2
            : window.innerWidth < 1400
              ? 3
              : 4
      setColumnCount(newColumnCount)
      // @ts-expect-error - this is okay for now
      gridRef.current?.resetAfterColumnIndex(0, true)
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const gridRef = useRef()

  const columnWidth = useCallback(
    () =>
      typeof window === 'undefined'
        ? 290
        : (window.innerWidth - 300) / columnCount,
    [columnCount, width],
  )

  const rowHeight = useCallback(() => 280, [])

  const allToolkits = useMemo(
    () => [...prodToolkits, ...betaToolkits],
    [prodToolkits, betaToolkits],
  )

  const data = useMemo(() => {
    const filteredToolkits =
      category === 'all' || category == null
        ? allToolkits
        : allToolkits.filter((toolkit) =>
            toolkit.meta.categories?.some(
              (cat) =>
                cat.name.toLowerCase() ===
                (APP_CATEGORY_KEYS_REVERSE[category] ?? '').toLowerCase(),
            ),
          )

    const list = search
      ? allToolkits.filter((toolkit) => {
          return (
            toolkit?.name?.toLowerCase().includes(search.toLowerCase()) ||
            (toolkit?.meta?.categories as unknown as string[])
              ?.join(' ')
              .toLowerCase()
              .includes(search.toLowerCase())
          )
        })
      : filteredToolkits

    const result = [
      {
        type: 'add_new_tools_card',
      },
      ...list.map((toolkit) => ({
        type: 'toolkit_card' as const,
        id: toolkit.slug,
        name: toolkit.name,
        description: toolkit.meta.description,
        logo: toolkit.meta.logo,
        categories: toolkit.meta.categories as { id: string; name: string }[],
        auth_schemes: (toolkit.auth_schemes ?? []).map((scheme) => ({
          mode: scheme,
        })),
        meta: {
          triggersCount: toolkit.meta.triggers_count || 0,
          actionsCount: toolkit.meta.tools_count || 0,
        },
      })),
    ]

    return result
  }, [allToolkits, search, category])

  const rowCount = useMemo(
    () => Math.ceil(data.length / columnCount),
    [data, columnCount],
  )

  const containerWidth = useMemo(() => {
    return width - 290 || 1400
  }, [width])

  const containerHeight = useMemo(() => {
    return height - 68 || 700
  }, [height])

  const toolkitCountByCategory = useMemo(() => {
    const toolkitCount = allToolkits.reduce(
      (acc, toolkit) => {
        toolkit.meta.categories.forEach((category) => {
          const categoryKey = APP_CATEGORY_KEYS[category.name.toLowerCase()]
          if (!categoryKey) return
          if (!acc[categoryKey]) acc[categoryKey] = 0

          acc[categoryKey]++
        })
        return acc
      },
      {} as Record<string, number>,
    )

    return {
      all: allToolkits?.length,
      ...toolkitCount,
    }
  }, [allToolkits])

  return (
    <>
      <FilterBar
        categoriesList={APP_CATEGORIES_LIST}
        appCountByCategory={toolkitCountByCategory}
      />
      <div className="w-full px-6 pr-0" id="container">
        {isSuccess && data.length > 0 && (
          <ErrorBoundary
            fallback={
              <div>{`Something went wrong. We'll work on getting this fixed.`}</div>
            }
          >
            <Grid
              columnCount={columnCount}
              columnWidth={columnWidth}
              height={containerHeight}
              rowCount={rowCount}
              rowHeight={rowHeight}
              overscanRowCount={5}
              width={containerWidth}
              // @ts-expect-error - this is okay for now
              ref={gridRef}
              className={clsx(
                scrollBarStyle,
                css`
                  margin-top: 49px;
                  padding-top: 40px;
                `,
              )}
            >
              {/* Pass the Cell component as children, providing necessary props */}
              {(props) => (
                <Cell
                  {...props} // Pass columnIndex, rowIndex, style
                  columnCount={columnCount} // Pass columnCount
                  data={data as ZTCard[]} // Pass data, asserting type if needed
                />
              )}
            </Grid>
          </ErrorBoundary>
        )}
        {isLoading && (
          <LoadingState
            height={containerHeight - 100}
            text="Fetching tools..."
          />
        )}
        {isError && <Error height={containerHeight} />}
        {!isLoading && !isError && data.length === 0 && (
          <NoResult
            height={containerHeight}
            text="No tools found"
            noResultAction={() => {}}
          />
        )}
      </div>
    </>
  )
}

export const AddNewToolsCard = () => {
  return (
    <Link href="/custom_tools">
      <div className="ds-border-gray-400 hover:ds-border-blue-600 css-1x93psa relative flex h-full cursor-pointer flex-col items-center justify-center rounded-[12px] border-[1px] bg-[#ffffff] px-[20px] py-[20px] text-[#d1d1d1a7] hover:bg-gradient-to-br hover:from-white hover:to-blue-50">
        <PlusSquare
          height={28}
          width={28}
          className="ds-text-blue-600"
          strokeWidth={1.7}
        />
        <div className="mt-[20px] text-[16px] font-[500] leading-none text-black-200">
          Setup a New Tool
        </div>
        <div className="mt-[12px] text-center text-[15px] text-black-500">
          Convert your APIs into a Tool,
          <br />
          or ask Composio to add a new Tool
        </div>
      </div>
    </Link>
  )
}

const AUTH_MODES = {
  API_KEY: 'API',
  OAUTH2: 'OAUTH2',
  BASIC: 'BASIC',
  BEARER_TOKEN: 'BEARER',
  OAUTH1: 'OAUTH1',
  NO_AUTH: 'NO_AUTH',
}

export function Badge({
  children,
  variant = 'default',
  className,
}: {
  children: React.ReactNode
  variant?: 'default' | 'outline'
  className?: string
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-3 py-1 text-[10px] font-medium',
        variant === 'default'
          ? 'bg-gray-100 text-gray-800'
          : 'border border-gray-200',
        className,
      )}
    >
      {children}
    </span>
  )
}

function AuthSchemes({
  schemes,
}: {
  schemes: (keyof typeof authSchemeConfig)[]
}) {
  if (!schemes) return null

  return (
    <div className="flex flex-wrap justify-end gap-2">
      {schemes.map((scheme: keyof typeof authSchemeConfig) => {
        let config = authSchemeConfig[scheme]
        if (!config) {
          config = authSchemeConfig.CUSTOM
        }
        const Icon = config.icon

        return (
          <motion.div
            key={scheme}
            whileHover={{ scale: 1.05 }}
            transition={{ type: 'spring', stiffness: 300 }}
          >
            <Badge
              className={`bg-gradient-to-r ${config.gradient} ${config.text} ${config.border} ${config.hoverGradient} flex items-center gap-1.5 shadow-sm transition-all`}
            >
              <Icon className="h-[12px] w-[12px]" />
              <div className="mt-[2px] flex items-center self-center align-text-top text-[10px] font-medium leading-[10px]">
                {AUTH_MODES[scheme as keyof typeof AUTH_MODES] ||
                  scheme?.split('_')[0]}
              </div>
            </Badge>
          </motion.div>
        )
      })}
    </div>
  )
}

function TypeBadges({ type }: { type: string }) {
  const updateQuery = UpdateQuery()
  const onBadgeClick = (e: React.MouseEvent) => {
    e.preventDefault()
    updateQuery('category', APP_CATEGORY_KEYS[type.toLowerCase()])
  }
  return (
    <motion.button
      key={type}
      className="pointer focus:outline-none"
      onClick={onBadgeClick}
    >
      <Badge
        variant="outline"
        className="cursor-pointer border-gray-200/80 bg-gray-50 text-[10px] font-medium text-gray-700 transition-all hover:border-gray-300/80"
      >
        {type}
      </Badge>
    </motion.button>
  )
}

const ToolkitBox = ({
  toolkitData,
}: {
  toolkitData: {
    name: string
    slug: string
    description: string
    logo: string
    categories: string[]
    auth_schemes: {
      mode: string
    }[]
    meta: {
      actionsCount: number
      triggersCount: number
    }
  }
}) => {
  // Auth only toolkits have 0 triggers and 0 actions
  const isAuthOnlyToolkit =
    !toolkitData?.meta?.actionsCount && !toolkitData?.meta?.triggersCount

  const supportedAuthModes =
    toolkitData?.auth_schemes?.map((scheme) => scheme.mode) || []
  const { push } = useRouter()

  return (
    <ErrorBoundary
      fallback={
        <div>{`Something went wrong. We'll work on getting this fixed.`}</div>
      }
    >
      <Link href={`/app/${toolkitData.slug}`}>
        <motion.div
          className={clsx(
            'ds-border-gray-400 hover:ds-border-blue-600 relative flex h-full cursor-pointer flex-col justify-between rounded-[12px] border-[1px] bg-[#ffffff] px-5 py-5 text-[#d1d1d1a7] hover:bg-gradient-to-br hover:from-white hover:to-blue-50',
            css`
              box-shadow:
                rgba(50, 50, 93, 0.02) 0px 2px 5px -1px,
                rgba(0, 0, 0, 0.05) 0px 1px 3px -1px;
              &:hover {
                box-shadow:
                  rgba(50, 50, 93, 0.03) 0px 4px 8px -2px,
                  rgba(0, 0, 0, 0.08) 0px 2px 4px -2px;
              }
            `,
          )}
          whileHover={{
            y: -1,
          }}
        >
          <div className="w-full">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div
                  className={clsx(
                    'flex h-8 w-8 items-center justify-center uppercase text-pink-200',
                    css`
                      background-image: url('${toolkitData.logo}');
                      background-color: rgba(243, 244, 246, 0.9);
                      background-size: 70% 70%; /* Increased padding by reducing background size more */
                      background-position: center;
                      padding: 4px;
                      border-radius: 4px;

                      :hover {
                        box-shadow:
                          lch(0 0 0 / 0.02) 0px 0px 3px -1px,
                          lch(0 0 0 / 0.02) 0px 0px 1px 1px;
                      }
                    `,
                    roundedImage,
                  )}
                ></div>
                <div className="text-[15px] font-[500] leading-none text-black-200">
                  {toolkitData.name.charAt(0).toUpperCase() +
                    toolkitData.name.slice(1)}
                </div>
              </div>

              <div className="flex flex-wrap justify-end gap-2 text-[11px] text-black-300">
                <AuthSchemes
                  schemes={
                    supportedAuthModes as (keyof typeof authSchemeConfig)[]
                  }
                />
              </div>
            </div>
            <div className="group relative">
              <p className="mb-1 mt-3 overflow-hidden text-ellipsis text-[14px] font-[400] leading-[170%] text-grey-600">
                {toolkitData.description.length > 100
                  ? `${toolkitData.description.substring(0, 100)}...`
                  : toolkitData.description}
              </p>
            </div>
          </div>
          <div>
            <div className="flex justify-between">
              <div className="flex flex-wrap gap-2 text-[11px] text-black-300">
                {toolkitData.categories
                  ?.filter((category) => category !== 'default')
                  .slice(0, 2)
                  .map((category) => {
                    if (APP_CATEGORY_KEYS[category]) {
                      return (
                        <TypeBadges
                          type={
                            category.charAt(0).toUpperCase() + category.slice(1)
                          }
                          key={category}
                        />
                      )
                    }
                  })}
              </div>
            </div>

            {isAuthOnlyToolkit ? (
              <motion.a
                href="/custom_tools"
                className="ds-text-blue-600 mt-4 inline-flex items-center gap-2 text-[12px] font-[12px] hover:text-blue-700"
                whileHover={{ scale: 1.02 }}
                transition={{ type: 'spring', stiffness: 300 }}
                onClick={(e) => {
                  e.preventDefault()
                  push('/custom_tools')
                }}
              >
                <Plus className="h-4 w-4" />
                Create custom tool
              </motion.a>
            ) : (
              <>
                <div className="mb-0 mt-4 flex w-full gap-2 text-black-400">
                  {toolkitData.meta.actionsCount > 0 && (
                    <div className="flex items-center gap-1 text-[13px]">
                      <Blocks height={14} width={14} color="#F6804D" />
                      <div>
                        <strong className="font-[500]">
                          {toolkitData.meta.actionsCount}
                        </strong>
                        {` `}
                        actions
                      </div>
                    </div>
                  )}
                  {toolkitData.meta.triggersCount > 0 && (
                    <div className="ml-2 flex items-center gap-1 text-[13px]">
                      <Workflow height={14} width={14} color="#5B4DF6" />
                      <div>
                        <strong className="font-[500]">
                          {toolkitData.meta.triggersCount}
                        </strong>
                        {` `}
                        triggers
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </motion.div>
      </Link>
    </ErrorBoundary>
  )
}

const roundedImage = css`
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
`
